import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/use-toast';
import { appsMutationLimiter, checkRateLimit } from '@/lib/rateLimiter';
import { awardPoints } from '@/lib/gamification';
import type { Tables, TablesInsert, TablesUpdate } from '@/integrations/supabase/types';

export type JobApplication = Tables<'job_applications'>;
export type JobApplicationInsert = TablesInsert<'job_applications'>;
export type JobApplicationUpdate = TablesUpdate<'job_applications'>;

export type AppStatus = 'applied' | 'screening' | 'interview' | 'technical' | 'offer' | 'accepted' | 'rejected';

// Statuses that represent forward progress in the pipeline
const ADVANCE_STATUSES: AppStatus[] = ['screening', 'interview', 'technical', 'offer', 'accepted'];

export const APP_STATUSES: { value: AppStatus; label: string; color: string }[] = [
  { value: 'applied', label: 'Applied', color: 'hsl(217, 91%, 60%)' },
  { value: 'screening', label: 'Screening', color: 'hsl(38, 92%, 50%)' },
  { value: 'interview', label: 'Interview', color: 'hsl(25, 95%, 53%)' },
  { value: 'technical', label: 'Technical', color: 'hsl(262, 83%, 58%)' },
  { value: 'offer', label: 'Offer', color: 'hsl(160, 84%, 39%)' },
  { value: 'accepted', label: 'Accepted', color: 'hsl(152, 69%, 31%)' },
  { value: 'rejected', label: 'Rejected', color: 'hsl(0, 84%, 60%)' },
];

export function useJobApplications() {
  const user = useAuthStore((s) => s.user);
  return useQuery({
    queryKey: ['job_applications', user?.id],
    queryFn: async () => {
      if (!user) return [];
      const { data, error } = await supabase
        .from('job_applications')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as JobApplication[];
    },
    enabled: !!user,
    retry: 2,
    staleTime: 30_000,
  });
}

export function useCreateJobApplication() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (app: Omit<JobApplicationInsert, 'user_id'>) => {
      checkRateLimit(appsMutationLimiter, 'createApplication');
      if (!user) throw new Error('Not authenticated');
      const { data, error } = await supabase
        .from('job_applications')
        .insert({ ...app, user_id: user.id })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job_applications'] });
      toast({ title: 'Application added' });
    },
    onError: (e) => toast({ title: 'Error adding application', description: e.message, variant: 'destructive' }),
  });
}

export function useUpdateJobApplication() {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const { toast } = useToast();
  return useMutation({
    mutationFn: async ({ id, ...updates }: JobApplicationUpdate & { id: string }) => {
      checkRateLimit(appsMutationLimiter, 'updateApplication');
      const { data, error } = await supabase
        .from('job_applications')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_, variables) => {
      qc.invalidateQueries({ queryKey: ['job_applications'] });
      toast({ title: 'Application updated' });
      // Award XP for advancing in the pipeline
      if (user?.id && variables.status && ADVANCE_STATUSES.includes(variables.status as AppStatus)) {
        awardPoints(user.id, 'app_advance').then(() => {
          qc.invalidateQueries({ queryKey: ['gamification'] });
        });
      }
    },
    onError: (e) => toast({ title: 'Error updating application', description: e.message, variant: 'destructive' }),
  });
}

export function useDeleteJobApplication() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: async (id: string) => {
      checkRateLimit(appsMutationLimiter, 'deleteApplication');
      const { error } = await supabase.from('job_applications').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['job_applications'] });
      toast({ title: 'Application deleted' });
    },
    onError: (e) => toast({ title: 'Error deleting application', description: e.message, variant: 'destructive' }),
  });
}
